import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { logError } from '../utils/log';

const gameStructure = [
    { name: '301 Singles', type: 'S301' }, { name: '301 Singles', type: 'S301' }, { name: '301 Doubles', type: 'D301' },
    { name: '501 Singles', type: 'S501' }, { name: '501 Singles', type: 'S501' }, { name: '501 Doubles', type: 'D501' },
    { name: 'Cricket Singles', type: 'SCricket' }, { name: 'Cricket Singles', type: 'SCricket' }, { name: 'Cricket Doubles', type: 'DCricket' },
];

const gameSortOptions = [
    { value: 'name', label: 'Name' },
    { value: 'totalGamesWon', label: 'Total Games Won' },
    { value: 'totalLegsWon', label: 'Total Legs Won' },
    { value: 'totalSinglesLegsWon', label: 'Total S Legs Won' },
    { value: 'S301.winPct', label: 'S301 Win %' },
    { value: 'D301.winPct', label: 'D301 Win %' },
    { value: 'S501.winPct', label: 'S501 Win %' },
    { value: 'D501.winPct', label: 'D501 Win %' },
    { value: 'SCricket.winPct', label: 'SCricket Win %' },
    { value: 'DCricket.winPct', label: 'DCricket Win %' },
    { value: 'weeksPlayed', label: 'Weeks Played' },
];

const highlightSortOptions = [
    { value: 'name', label: 'Name' },
    { value: 'totalHighlights', label: 'Total Highlights' },
    { value: 'totalTons', label: 'Total Tons' },
    { value: 'highestTon', label: 'Highest Ton' },
    { value: 'total180s', label: '180s' },
    { value: 'highestIn', label: 'High In' },
    { value: 'highestOut', label: 'High Out' },
    { value: 'totalC9', label: '9-Hits' },
    { value: 'totalC8', label: '8-Hits' },
    { value: 'totalC7', label: '7-Hits' },
    { value: 'totalC6', label: '6-Hits' },
];

const allSortOptions = [
    ...gameSortOptions,
    ...highlightSortOptions.filter(opt => opt.value !== 'name')
];

export const PlayerStatsCardsPage = () => {
    const [playerStats, setPlayerStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('game'); // 'game', 'highlight', 'all'
    const [sortConfig, setSortConfig] = useState({ key: 'totalGamesWon', direction: 'desc' });
    const [selectedPlayerForHistory, setSelectedPlayerForHistory] = useState(null);
    const [selectedPlayerForHighlights, setSelectedPlayerForHighlights] = useState(null);
    const [allSchedules, setAllSchedules] = useState([]);
    const [selectedScheduleId, setSelectedScheduleId] = useState('');

    const currentSortOptions = useMemo(() => {
        if (view === 'game') return gameSortOptions;
        if (view === 'highlight') return highlightSortOptions;
        return allSortOptions;
    }, [view]);

    useEffect(() => {
        setSortConfig(prev => {
            if (!currentSortOptions.find(opt => opt.value === prev.key)) {
                return { key: currentSortOptions[1]?.value || 'name', direction: 'desc' };
            }
            return prev;
        });
    }, [currentSortOptions]);

    const renderSortIndicator = (key) => {
        if (sortConfig.key === key) {
            return <span className="text-info ml-1 text-[10px]">{sortConfig.direction === 'desc' ? '▼' : '▲'}</span>;
        }
        return null;
    };

    useEffect(() => {
        const fetchSchedules = async () => {
            setLoading(true);
            try {
                const schedulesSnapshot = await getDocs(collection(db, "schedules"));
                const schedulesList = schedulesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                schedulesList.sort((a, b) => {
                    if (a.archived !== b.archived) return a.archived ? 1 : -1;
                    return new Date(b.createdAt) - new Date(a.createdAt);
                });
                setAllSchedules(schedulesList);

                if (schedulesList.length > 0) {
                    const latestNonArchived = schedulesList.find(s => !s.archived);
                    setSelectedScheduleId(latestNonArchived ? latestNonArchived.id : schedulesList[0].id);
                } else {
                    setLoading(false);
                }
            } catch (error) {
                logError("Error fetching schedules: ", error);
                setLoading(false);
            }
        };
        fetchSchedules();
    }, []);

    useEffect(() => {
        if (!selectedScheduleId || allSchedules.length === 0) {
            if (allSchedules.length > 0) setLoading(false);
            return;
        }
        const calculateStats = async () => {
            setLoading(true);
            try {
                const currentSchedule = allSchedules.find(s => s.id === selectedScheduleId);
                
                // Derive participating player IDs directly from match results for accuracy
                const participatingMemberIds = new Set();
                if (currentSchedule && Array.isArray(currentSchedule.weeks)) {
                    currentSchedule.weeks.forEach(week => {
                        (week.games || []).forEach(match => {
                            if (match.results?.playerAssignments) {
                                Object.values(match.results.playerAssignments).forEach(playerId => {
                                    if (playerId) participatingMemberIds.add(playerId);
                                });
                            }
                        });
                    });
                }

                // Fetch users and filter to non-archived players participating in current season
                const usersSnapshot = await getDocs(collection(db, "users"));
                const playersData = usersSnapshot.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .filter(u => !u.archived && participatingMemberIds.has(u.id));

                const stats = {};
                playersData.forEach(player => {
                    stats[player.id] = {
                        id: player.id,
                        name: player.nickname ? `${player.nickname} (${player.firstName} ${player.lastName})` : `${player.firstName} ${player.lastName}`,
                        totalGamesPlayed: 0, totalGamesWon: 0,
                        weeksPlayed: new Set(),
                        totalLegsPlayed: 0, totalLegsWon: 0,
                        totalSinglesLegsPlayed: 0, totalSinglesLegsWon: 0,
                        gameHistory: [],
                        highlightHistory: [],
                        S301: { w: 0, l: 0, legsWon: 0, legsPlayed: 0, winPct: 0 }, D301: { w: 0, l: 0, legsWon: 0, legsPlayed: 0, winPct: 0 },
                        S501: { w: 0, l: 0, legsWon: 0, legsPlayed: 0, winPct: 0 }, D501: { w: 0, l: 0, legsWon: 0, legsPlayed: 0, winPct: 0 },
                        SCricket: { w: 0, l: 0, legsWon: 0, legsPlayed: 0, winPct: 0 }, DCricket: { w: 0, l: 0, legsWon: 0, legsPlayed: 0, winPct: 0 },
                        totalHighlights: 0,
                        totalTons: 0,
                        highestTon: 0,
                        total180s: 0,
                        totalHighIns: 0,
                        highestIn: 0,
                        highestOut: 0,
                        totalHighOuts: 0,
                        totalC6: 0,
                        totalC7: 0,
                        totalC8: 0,
                        totalC9: 0,
                    };
                });

                if (currentSchedule) {
                    currentSchedule.weeks.forEach(week => {
                        const weekIdentifier = `${currentSchedule.id}-${week.week}`;
                        week.games.forEach(match => {
                            if (match.eventName) return;
                            if (match.results) {
                                match.results.gameScores.forEach((score, idx) => {
                                    const gameType = gameStructure[idx].type;
                                    const isSingles = gameType.startsWith('S');
                                    const homePlayers = isSingles ? [match.results.playerAssignments[`${idx}-home-p1`]] : [match.results.playerAssignments[`${idx}-home-p1`], match.results.playerAssignments[`${idx}-home-p2`]];
                                    const awayPlayers = isSingles ? [match.results.playerAssignments[`${idx}-away-p1`]] : [match.results.playerAssignments[`${idx}-away-p1`], match.results.playerAssignments[`${idx}-away-p2`]];

                                    const gameWonByHome = score.home > score.away;

                                    const getSnapshot = (pid) => {
                                        if (!stats[pid]) return null;
                                        const s = stats[pid][gameType];
                                        return { w: s.w, l: s.l, legsWon: s.legsWon, legsPlayed: s.legsPlayed, winPct: s.legsPlayed > 0 ? s.legsWon / s.legsPlayed : 0 };
                                    };
                                    const homeRecordsBefore = {};
                                    homePlayers.filter(Boolean).forEach(pid => { homeRecordsBefore[pid] = getSnapshot(pid); });
                                    const awayRecordsBefore = {};
                                    awayPlayers.filter(Boolean).forEach(pid => { awayRecordsBefore[pid] = getSnapshot(pid); });

                                    homePlayers.filter(Boolean).forEach(playerId => {
                                        if (!stats[playerId]) return;
                                        stats[playerId].totalGamesPlayed++;
                                        stats[playerId].weeksPlayed.add(weekIdentifier);
                                        stats[playerId][gameType].legsPlayed += score.home + score.away;
                                        stats[playerId].totalLegsPlayed += score.home + score.away;
                                        if (isSingles) {
                                            stats[playerId].totalSinglesLegsPlayed += score.home + score.away;
                                            stats[playerId].totalSinglesLegsWon += score.home;
                                        }
                                        stats[playerId][gameType].legsWon += score.home;
                                        stats[playerId].totalLegsWon += score.home;
                                        if (gameWonByHome) {
                                            stats[playerId].totalGamesWon++;
                                            stats[playerId][gameType].w++;
                                        } else {
                                            stats[playerId][gameType].l++;
                                        }

                                        stats[playerId].gameHistory.push({
                                            week: week.week,
                                            date: week.date,
                                            gameName: gameStructure[idx].name,
                                            gameType: gameStructure[idx].type,
                                            isSingles: isSingles,
                                            playerLegs: score.home,
                                            opponentLegs: score.away,
                                            won: gameWonByHome,
                                            opponentIds: awayPlayers.filter(Boolean),
                                            playerRecordAtTime: homeRecordsBefore[playerId] || { w: 0, l: 0, legsWon: 0, legsPlayed: 0, winPct: 0 },
                                            opponentRecordsAtTime: awayPlayers.filter(Boolean).map(oppId => awayRecordsBefore[oppId] || { w: 0, l: 0, legsWon: 0, legsPlayed: 0, winPct: 0 })
                                        });
                                    });

                                    awayPlayers.filter(Boolean).forEach(playerId => {
                                        if (!stats[playerId]) return;
                                        stats[playerId].totalGamesPlayed++;
                                        stats[playerId].weeksPlayed.add(weekIdentifier);
                                        stats[playerId][gameType].legsPlayed += score.home + score.away;
                                        stats[playerId].totalLegsPlayed += score.home + score.away;
                                        if (isSingles) {
                                            stats[playerId].totalSinglesLegsPlayed += score.home + score.away;
                                            stats[playerId].totalSinglesLegsWon += score.away;
                                        }
                                        stats[playerId][gameType].legsWon += score.away;
                                        stats[playerId].totalLegsWon += score.away;
                                        if (!gameWonByHome) {
                                            stats[playerId].totalGamesWon++;
                                            stats[playerId][gameType].w++;
                                        } else {
                                            stats[playerId][gameType].l++;
                                        }

                                        stats[playerId].gameHistory.push({
                                            week: week.week,
                                            date: week.date,
                                            gameName: gameStructure[idx].name,
                                            gameType: gameStructure[idx].type,
                                            isSingles: isSingles,
                                            playerLegs: score.away,
                                            opponentLegs: score.home,
                                            won: !gameWonByHome,
                                            opponentIds: homePlayers.filter(Boolean),
                                            playerRecordAtTime: awayRecordsBefore[playerId] || { w: 0, l: 0, legsWon: 0, legsPlayed: 0, winPct: 0 },
                                            opponentRecordsAtTime: homePlayers.filter(Boolean).map(oppId => homeRecordsBefore[oppId] || { w: 0, l: 0, legsWon: 0, legsPlayed: 0, winPct: 0 })
                                        });
                                    });
                                });

                                if (match.results.highlights) {
                                    Object.entries(match.results.highlights).forEach(([playerId, playerHighlights]) => {
                                        let currentHighlights = 0;
                                        if (stats[playerId]) {
                                            if (playerHighlights.tons) {
                                                const tons = playerHighlights.tons.split(',').map(t => parseInt(t.trim(), 10)).filter(t => !isNaN(t));
                                                stats[playerId].totalTons += tons.length;
                                                currentHighlights += tons.length;
                                                const maxTon = Math.max(...tons);
                                                if (maxTon > stats[playerId].highestTon) {
                                                    stats[playerId].highestTon = maxTon;
                                                }
                                            }
                                            if (playerHighlights['180s']) {
                                                const count = parseInt(playerHighlights['180s'], 10);
                                                stats[playerId].total180s += count;
                                                currentHighlights += count;
                                            }
                                            if (playerHighlights.highOut) {
                                                const highOuts = playerHighlights.highOut.split(',').map(t => parseInt(t.trim(), 10)).filter(t => !isNaN(t));
                                                stats[playerId].totalHighOuts += highOuts.length;
                                                currentHighlights += highOuts.length;
                                                const maxOut = Math.max(...highOuts);
                                                if (maxOut > stats[playerId].highestOut) {
                                                    stats[playerId].highestOut = maxOut;
                                                }
                                            }
                                            if (playerHighlights.on80) {
                                                const highIns = playerHighlights.on80.split(',').map(t => parseInt(t.trim(), 10)).filter(t => !isNaN(t));
                                                stats[playerId].totalHighIns += highIns.length;
                                                currentHighlights += highIns.length;
                                                const maxIn = Math.max(...highIns);
                                                if (maxIn > stats[playerId].highestIn) {
                                                    stats[playerId].highestIn = maxIn;
                                                }
                                            }
                                            if (playerHighlights.c6) stats[playerId].totalC6 += parseInt(playerHighlights.c6, 10);
                                            if (playerHighlights.c7) stats[playerId].totalC7 += parseInt(playerHighlights.c7, 10);
                                            if (playerHighlights.c8) stats[playerId].totalC8 += parseInt(playerHighlights.c8, 10);
                                            if (playerHighlights.c9) stats[playerId].totalC9 += parseInt(playerHighlights.c9, 10);

                                            currentHighlights += parseInt(playerHighlights.c6 || 0, 10);
                                            currentHighlights += parseInt(playerHighlights.c7 || 0, 10);
                                            currentHighlights += parseInt(playerHighlights.c8 || 0, 10);
                                            currentHighlights += parseInt(playerHighlights.c9 || 0, 10);

                                            stats[playerId].totalHighlights += currentHighlights;

                                            if (currentHighlights > 0) {
                                                stats[playerId].highlightHistory.push({
                                                    week: week.week,
                                                    date: week.date,
                                                    tons: playerHighlights.tons || '',
                                                    '180s': playerHighlights['180s'] || '',
                                                    highIn: playerHighlights.on80 || '',
                                                    highOut: playerHighlights.highOut || '',
                                                    c9: playerHighlights.c9 || '',
                                                    c8: playerHighlights.c8 || '',
                                                    c7: playerHighlights.c7 || '',
                                                    c6: playerHighlights.c6 || '',
                                                    total: currentHighlights
                                                });
                                            }
                                        }
                                    });
                                }
                            }
                        });
                    });
                }

                const statsArray = Object.values(stats);

                statsArray.forEach(player => {
                    player.weeksPlayed = player.weeksPlayed.size;
                    Object.keys(player).forEach(key => {
                        if (typeof player[key] === 'object' && player[key] !== null && 'legsPlayed' in player[key]) {
                            const totalLegs = player[key].legsPlayed;
                            player[key].winPct = totalLegs > 0 ? (player[key].legsWon / totalLegs) : 0;
                        }
                    });
                });
                setPlayerStats(statsArray);

            } catch (error) {
                logError("Error calculating player stats: ", error);
            }
            setLoading(false);
        };

        calculateStats();
    }, [selectedScheduleId, allSchedules]);

    const sortedPlayerStats = useMemo(() => {
        let sortableItems = [...playerStats];
        if (sortConfig !== null) {
            sortableItems.sort((a, b) => {
                const keys = sortConfig.key.split('.');
                const aValue = keys.reduce((obj, key) => obj && obj[key], a);
                const bValue = keys.reduce((obj, key) => obj && obj[key], b);

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [playerStats, sortConfig]);

    if (loading) {
        return <div className="text-center">Loading player stats...</div>;
    }

    return (
        <div className="space-y-6">
            <h2 className="page-title heading-primary-1 text-4xl font-extrabold text-center mb-6">Player Statistics</h2>
            
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-6">
                <div className="flex gap-2 items-center w-full sm:w-auto">
                    <label htmlFor="season-select" className="text-sm font-semibold text-muted whitespace-nowrap">Season:</label>
                    <select
                        id="season-select"
                        value={selectedScheduleId}
                        onChange={e => setSelectedScheduleId(e.target.value)}
                        className="select-field p-2 w-full sm:w-auto"
                        disabled={loading || allSchedules.length === 0}
                    >
                        {allSchedules.map(s => (
                            <option key={s.id} value={s.id}>
                                {s.name} {s.archived ? '(Archived)' : ''}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                    <button onClick={() => setView('game')} className={`flex-1 sm:flex-none ${view === 'game' ? 'segmented-btn-active' : 'segmented-btn'}`}>Game</button>
                    <button onClick={() => setView('highlight')} className={`flex-1 sm:flex-none ${view === 'highlight' ? 'segmented-btn-active' : 'segmented-btn'}`}>Highlight</button>
                    <button onClick={() => setView('all')} className={`flex-1 sm:flex-none ${view === 'all' ? 'segmented-btn-active' : 'segmented-btn'}`}>All</button>
                </div>
                
                <div className="flex gap-2 items-center w-full sm:w-auto">
                    <label className="text-sm font-semibold text-muted whitespace-nowrap">Sort By:</label>
                    <select 
                        value={sortConfig.key} 
                        onChange={(e) => setSortConfig({ ...sortConfig, key: e.target.value })} 
                        className="select-field p-2 w-full sm:w-auto"
                    >
                        {currentSortOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                    <button 
                        onClick={() => setSortConfig({ ...sortConfig, direction: sortConfig.direction === 'asc' ? 'desc' : 'asc' })} 
                        className="btn btn-secondary px-3"
                        title="Toggle sort direction"
                    >
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </button>
                </div>
            </div>

            {allSchedules.find(s => s.id === selectedScheduleId)?.archived && (<p className="text-center text-sm text-warning -mt-4">You are viewing stats for an archived season.</p>)}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {sortedPlayerStats.map((player, index) => (
                    <div key={player.id} className="card rounded-lg shadow-md p-4 sm:p-5 flex flex-col gap-4">
                        <div className="flex justify-between items-center border-b border-low pb-3">
                            <div className="flex items-center gap-2 overflow-hidden mr-2">
                                <span className="tag tag-primary-one shrink-0 shadow-sm" title="Rank">#{index + 1}</span>
                                <h3 className="text-xl font-bold heading-primary-1 truncate flex items-center" title={player.name}>{player.name}{renderSortIndicator('name')}</h3>
                            </div>
                            <span className="tag tag-primary-one shrink-0 flex items-center">WP: {player.weeksPlayed}{renderSortIndicator('weeksPlayed')}</span>
                        </div>
                        
                        {(view === 'game' || view === 'all') && (
                            <div className="flex flex-col md:flex-row gap-4">
                                <div className="flex-1 surface-low p-3 rounded-lg border border-low">
                                    <h4 className="font-semibold text-muted text-xs uppercase tracking-wider mb-2">Overall Games & Legs</h4>
                                    <div className="space-y-1 text-sm">
                                        <div 
                                            className="flex justify-between p-1 -mx-1 rounded hover:bg-black/5 dark:hover:bg-white/5 cursor-pointer transition-colors"
                                            onClick={() => setSelectedPlayerForHistory({ player, filter: 'all', display: 'games' })}
                                        >
                                            <span className="text-muted flex items-center">Games (W-L){renderSortIndicator('totalGamesWon')}</span> 
                                            <span className="font-bold text-strong">{player.totalGamesWon}-{player.totalGamesPlayed - player.totalGamesWon}</span>
                                        </div>
                                        <div 
                                            className="flex justify-between p-1 -mx-1 rounded hover:bg-black/5 dark:hover:bg-white/5 cursor-pointer transition-colors"
                                            onClick={() => setSelectedPlayerForHistory({ player, filter: 'all', display: 'legs' })}
                                        >
                                            <span className="text-muted flex items-center">Legs (W-L){renderSortIndicator('totalLegsWon')}</span> 
                                            <span className="font-bold text-strong">{player.totalLegsWon}-{player.totalLegsPlayed - player.totalLegsWon}</span>
                                        </div>
                                        <div 
                                            className="flex justify-between p-1 -mx-1 rounded hover:bg-black/5 dark:hover:bg-white/5 cursor-pointer transition-colors"
                                            onClick={() => setSelectedPlayerForHistory({ player, filter: 'singles', display: 'legs' })}
                                        >
                                            <span className="text-muted flex items-center">Singles Legs (W-L){renderSortIndicator('totalSinglesLegsWon')}</span> 
                                            <span className="font-bold text-info">{player.totalSinglesLegsWon}-{player.totalSinglesLegsPlayed - player.totalSinglesLegsWon}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex-[2] surface-low p-3 rounded-lg border border-low">
                                    <h4 className="font-semibold text-muted text-xs uppercase tracking-wider mb-2">Record (Leg Win%)</h4>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 text-xs sm:text-sm">
                                        {['S301', 'D301', 'S501', 'D501', 'SCricket', 'DCricket'].map(type => (
                                            <div 
                                                key={type} 
                                                className="flex flex-col p-1.5 rounded hover:bg-black/5 dark:hover:bg-white/5 cursor-pointer transition-colors" 
                                                onClick={() => setSelectedPlayerForHistory({ player, filter: type, display: 'games' })}
                                            >
                                                <span className="font-semibold text-muted flex items-center">{type}{renderSortIndicator(`${type}.winPct`)}</span>
                                                <span className="font-bold text-strong">{player[type].w}-{player[type].l} <span className="text-muted font-normal text-xs ml-1">({player[type].winPct.toFixed(3)})</span></span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {(view === 'highlight' || view === 'all') && (
                            <div 
                                className="surface-low p-3 rounded-lg border border-low cursor-pointer hover:opacity-80 transition-opacity" 
                                onClick={() => setSelectedPlayerForHighlights(player)}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-semibold text-muted text-xs uppercase tracking-wider">Highlights</h4>
                                    <span className="tag tag-warning text-xs font-bold shrink-0 flex items-center">Total: {player.totalHighlights}{renderSortIndicator('totalHighlights')}</span>
                                </div>
                                <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 text-xs text-center">
                                    <div className="flex flex-col"><span className="text-muted flex justify-center items-center">Tons{renderSortIndicator('totalTons')}</span><span className="font-bold text-strong">{player.totalTons}</span></div>
                                    <div className="flex flex-col"><span className="text-muted flex justify-center items-center">High Ton{renderSortIndicator('highestTon')}</span><span className="font-bold text-strong">{player.highestTon || '-'}</span></div>
                                    <div className="flex flex-col"><span className="text-muted flex justify-center items-center">180s{renderSortIndicator('total180s')}</span><span className="font-bold text-strong">{player.total180s}</span></div>
                                    <div className="flex flex-col"><span className="text-muted flex justify-center items-center">High In{renderSortIndicator('highestIn')}</span><span className="font-bold text-strong">{player.highestIn || '-'}</span></div>
                                    <div className="flex flex-col"><span className="text-muted flex justify-center items-center">High Out{renderSortIndicator('highestOut')}</span><span className="font-bold text-strong">{player.highestOut || '-'}</span></div>
                                    <div className="flex flex-col"><span className="text-muted flex justify-center items-center">9-Hits{renderSortIndicator('totalC9')}</span><span className="font-bold text-strong">{player.totalC9}</span></div>
                                    <div className="flex flex-col"><span className="text-muted flex justify-center items-center">8-Hits{renderSortIndicator('totalC8')}</span><span className="font-bold text-strong">{player.totalC8}</span></div>
                                    <div className="flex flex-col"><span className="text-muted flex justify-center items-center">7{renderSortIndicator('totalC7')}/6-Hits{renderSortIndicator('totalC6')}</span><span className="font-bold text-strong">{player.totalC7}/{player.totalC6}</span></div>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {selectedPlayerForHistory && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 bg-black bg-opacity-60" onClick={() => setSelectedPlayerForHistory(null)}>
                    <div className="card rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center p-3 sm:p-4 border-b border-low">
                            <h3 className="text-xl font-bold heading-primary-1">
                                {selectedPlayerForHistory.player.name}
                            </h3>
                            <button onClick={() => setSelectedPlayerForHistory(null)} className="text-muted hover:text-strong text-2xl leading-none">&times;</button>
                        </div>
                        <div className="p-2 sm:p-4 overflow-y-auto space-y-3">
                            {(() => {
                                const filteredHistory = selectedPlayerForHistory.player.gameHistory.filter(game => {
                                    const filterType = selectedPlayerForHistory.filter;
                                    if (filterType === 'all') return true;
                                    if (filterType === 'singles') return game.isSingles;
                                    return game.gameType === filterType;
                                });

                                if (filteredHistory.length === 0) {
                                    return <p className="text-center text-muted py-4">No match history available.</p>;
                                }
                                return filteredHistory.map((game, i) => {
                                    const opponents = game.opponentIds.map(id => playerStats.find(p => p.id === id)).filter(Boolean);
                                    const oppNames = opponents.map(o => o.name).join(' & ');
                                    
                                    let playerRecordDisplay = 'N/A';
                                    let oppRecordDisplay = 'N/A';
                                    let highlightClass = '';

                                    if (game.playerRecordAtTime) {
                                        playerRecordDisplay = `${game.playerRecordAtTime.w}-${game.playerRecordAtTime.l}`;
                                    }

                                    if (game.opponentRecordsAtTime && game.opponentRecordsAtTime.length > 0) {
                                        const opp1Record = game.opponentRecordsAtTime[0];
                                        oppRecordDisplay = `${opp1Record.w}-${opp1Record.l}`;
                                        
                                        let compPct = opp1Record.winPct;
                                        
                                        if (game.opponentRecordsAtTime.length > 1 && game.opponentRecordsAtTime[1]) {
                                            const opp2Record = game.opponentRecordsAtTime[1];
                                            if (opp2Record) {
                                                oppRecordDisplay = `${opp1Record.w}-${opp1Record.l} & ${opp2Record.w}-${opp2Record.l}`;
                                                compPct = (opp1Record.winPct + opp2Record.winPct) / 2;
                                            }
                                        }

                                        if (game.playerRecordAtTime) {
                                            if (game.won && game.playerRecordAtTime.winPct < compPct) {
                                                highlightClass = 'soft-success';
                                            } else if (!game.won && game.playerRecordAtTime.winPct > compPct) {
                                                highlightClass = 'soft-danger';
                                            }
                                        }
                                    }

                                    return (
                                        <div key={i} className={`p-3 rounded-lg border border-low flex flex-col sm:flex-row justify-between gap-3 ${highlightClass || 'surface-low'}`}>
                                            <div className="flex flex-col justify-center">
                                                <span className="font-bold text-sm text-strong">Week {game.week} - {selectedPlayerForHistory.filter !== 'all' ? game.gameName.replace(' Singles', '') : game.gameName}</span>
                                                <span className="text-xs text-muted">{game.date}</span>
                                            </div>
                                            <div className="flex items-center justify-between sm:justify-end gap-6 sm:w-2/3">
                                                <div className="flex flex-col text-left sm:text-right">
                                                    <span className="text-sm font-semibold text-strong">vs {oppNames || 'Unknown'}</span>
                                                    <span className="text-xs text-muted">Player Record: {playerRecordDisplay}</span>
                                                    <span className="text-xs text-muted">Opp Record: {oppRecordDisplay}</span>
                                                </div>
                                                <div className="text-center shrink-0">
                                                    <span className={`text-2xl font-extrabold ${game.won ? 'text-success' : 'text-danger'}`}>
                                                        {game.won ? 'W' : 'L'}
                                                    </span>
                                                    <span className="text-sm font-bold ml-1 text-strong block sm:inline">
                                                        ({game.playerLegs}-{game.opponentLegs})
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                        <div className="p-3 sm:p-4 border-t border-low surface-low rounded-b-lg">
                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-6 text-[10px] sm:text-xs text-muted justify-center sm:justify-start">
                                <div className="flex items-center">
                                    <span className="w-3 h-3 inline-block soft-success mr-1.5 rounded-sm"></span> Won vs better record
                                </div>
                                <div className="flex items-center">
                                    <span className="w-3 h-3 inline-block soft-danger mr-1.5 rounded-sm"></span> Lost vs worse record
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {selectedPlayerForHighlights && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 bg-black bg-opacity-60" onClick={() => setSelectedPlayerForHighlights(null)}>
                    <div className="card rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center p-3 sm:p-4 border-b border-low">
                            <h3 className="text-xl font-bold heading-primary-1">
                                {selectedPlayerForHighlights.name} - Highlights
                            </h3>
                            <button onClick={() => setSelectedPlayerForHighlights(null)} className="text-muted hover:text-strong text-2xl leading-none">&times;</button>
                        </div>
                        <div className="p-2 sm:p-4 overflow-y-auto space-y-3">
                            {selectedPlayerForHighlights.highlightHistory && selectedPlayerForHighlights.highlightHistory.length > 0 ? (
                                selectedPlayerForHighlights.highlightHistory.map((h, i) => (
                                    <div key={i} className="p-3 surface-low rounded-lg border border-low">
                                        <div className="flex justify-between items-center mb-3 border-b border-low pb-2">
                                            <span className="font-bold text-strong">Week {h.week}</span>
                                            <span className="tag tag-info text-xs font-bold">Total: {h.total}</span>
                                        </div>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                                            {h.tons && <div><span className="text-muted text-xs block uppercase tracking-wider">Tons</span><span className="font-semibold text-strong">{h.tons}</span></div>}
                                            {h['180s'] && <div><span className="text-muted text-xs block uppercase tracking-wider">180s</span><span className="font-semibold text-strong">{h['180s']}</span></div>}
                                            {h.highIn && <div><span className="text-muted text-xs block uppercase tracking-wider">High In</span><span className="font-semibold text-strong">{h.highIn}</span></div>}
                                            {h.highOut && <div><span className="text-muted text-xs block uppercase tracking-wider">High Out</span><span className="font-semibold text-strong">{h.highOut}</span></div>}
                                            {h.c9 && <div><span className="text-muted text-xs block uppercase tracking-wider">9-Hits</span><span className="font-semibold text-strong">{h.c9}</span></div>}
                                            {h.c8 && <div><span className="text-muted text-xs block uppercase tracking-wider">8-Hits</span><span className="font-semibold text-strong">{h.c8}</span></div>}
                                            {h.c7 && <div><span className="text-muted text-xs block uppercase tracking-wider">7-Hits</span><span className="font-semibold text-strong">{h.c7}</span></div>}
                                            {h.c6 && <div><span className="text-muted text-xs block uppercase tracking-wider">6-Hits</span><span className="font-semibold text-strong">{h.c6}</span></div>}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-center text-muted py-4">No highlights recorded.</p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};